import React, { useState } from "react";
import Layout from "../../components/Layout";
import { useHotel } from "../../contexts/HotelContext";
import { useToast } from "../../contexts/ToastContext";
import { useWatermarkRecovery } from "../../hooks/useWatermarkRecovery";
import {
  MAX_SUSPECT_TEXT_LENGTH,
  RecoverWatermarkResponse,
} from "../../services/watermarkRecoveryApi";

/**
 * Generic "invisible / formatting characters present" detector. Used only for a
 * yes/no hint before submit so the admin knows immediately whether a pasted
 * copy still carries hidden characters (a retyped/stripped copy has none).
 *
 * Deliberately a BROAD Unicode format-char range, NOT the watermark's specific
 * zero-width alphabet — the exact alphabet and token length are CONFIDENTIAL
 * (ADR-087) and must never ship in the public frontend bundle. We never show
 * the count here either (that would leak the token length); only presence.
 */
const HAS_HIDDEN_CHARS = /[\u00AD\u200B-\u200F\u2060-\u2064\uFEFF]/;

/**
 * Explains the meaning and limits of the recovered "copy day" — shown next to
 * both the resolved result and each AMBIGUOUS candidate. Written for a
 * forensic expert / lawyer reading the panel: it is a probable-download day
 * derived from server logs, not a confirmed act of copying, and it is a UTC
 * calendar day (an access late at night Polish time can land on the previous
 * UTC day).
 */
const COPY_DAY_HINT =
  "Dzień (UTC), w którym owatermarkowana treść została zserwowana temu kontu — " +
  "prawdopodobny dzień pobrania, nie potwierdzony fakt kopiowania. UTC ≠ czas " +
  "polski: dostęp między 00:00 a ~02:00 czasu polskiego wypada na poprzedni dzień UTC.";

const REASON_MESSAGES: Record<string, { title: string; detail: string }> = {
  NO_MATCH: {
    title: "Nie znaleziono dopasowania",
    detail:
      "W tekście nie wykryto znaku wodnego pasującego do żadnego użytkownika. " +
      "Możliwe, że kopia została przepisana ręcznie (ukryte znaki zniknęły), " +
      "pochodzi sprzed wdrożenia znakowania, albo mieści się poza zakresem dat.",
  },
  NORMALIZED_BEYOND_RECOVERY: {
    title: "Znak wodny uszkodzony",
    detail:
      "Wykryto ślady ukrytych znaków, ale tekst został zmodyfikowany " +
      "(normalizacja / edycja) na tyle, że fingerprintu nie da się odtworzyć.",
  },
  AMBIGUOUS: {
    title: "Wynik niejednoznaczny",
    detail:
      "Token pasuje do więcej niż jednego użytkownika w przeszukiwanym oknie " +
      "czasu. Zawęź zakres dat, aby rozstrzygnąć, lub skoreluj kandydatów z logami dostępu.",
  },
};

/**
 * Maps an error thrown by the recovery mutation (usually an AxiosError) to a
 * Polish, admin-facing message. Covers the documented status codes of
 * POST /admin/grants/watermark/recover (400/403/429/503).
 */
function mapRecoveryError(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  switch (status) {
    case 400:
      return "Nieprawidłowe dane wejściowe (tekst pusty lub dłuższy niż limit).";
    case 403:
      return "Brak uprawnień administratora do tej operacji.";
    case 429:
      return "Przekroczono limit zapytań (10/min). Odczekaj chwilę i spróbuj ponownie.";
    case 503:
      return "Mechanizm znaku wodnego nie jest skonfigurowany na serwerze (brak sekretu).";
    default:
      return "Nie udało się wykonać odzyskiwania. Spróbuj ponownie za chwilę.";
  }
}

const WatermarkRecovery: React.FC = () => {
  const { user } = useHotel();
  const { addToast } = useToast();
  const mutation = useWatermarkRecovery();

  const [suspectText, setSuspectText] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [result, setResult] = useState<RecoverWatermarkResponse | null>(null);

  // Belt-and-suspenders: route is already AdminRoute-gated, but re-check here.
  if (user?.role !== "admin") {
    return (
      <Layout>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <h3 className="text-amber-800 font-medium">Brak dostępu</h3>
          <p className="text-amber-700 text-sm mt-1">
            Ta strona jest dostępna tylko dla globalnych administratorów.
          </p>
        </div>
      </Layout>
    );
  }

  const length = suspectText.length;
  const hasHidden = length > 0 && HAS_HIDDEN_CHARS.test(suspectText);
  const canSubmit = suspectText.trim().length > 0 && !mutation.isPending;

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      addToast({ message: `Skopiowano ${label}`, type: "success" });
    } catch {
      addToast({ message: "Nie udało się skopiować do schowka", type: "error" });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setResult(null);
    mutation.mutate(
      {
        suspectText,
        since: since || undefined,
        until: until || undefined,
      },
      {
        onSuccess: (data) => {
          setResult(data);
          if (data.resolved) {
            addToast({
              message:
                "Odnaleziono użytkownika, do którego prowadzi znak wodny.",
              type: "success",
            });
          }
        },
        onError: (error) => {
          addToast({ message: mapRecoveryError(error), type: "error" });
        },
      }
    );
  };

  const handleReset = () => {
    setSuspectText("");
    setSince("");
    setUntil("");
    setResult(null);
    mutation.reset();
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            Odzyskiwanie znaku wodnego
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            Wklej tekst znaleziony u konkurencji (np. tytuł naboru), aby ustalić,
            do którego użytkownika prowadzi ukryty znak wodny.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-800">
          <p className="font-medium">Jak to działa</p>
          <ul className="list-disc list-inside mt-1 space-y-1 text-blue-700">
            <li>
              Wklej tekst <strong>dokładnie tak, jak został skopiowany</strong>{" "}
              (Ctrl+C → Ctrl+V). Ręczne przepisanie usuwa niewidoczne znaki.
            </li>
            <li>
              Zakres dat jest opcjonalny — zawęża okno wyszukiwania, gdy wiesz,
              kiedy nastąpiła kopia.
            </li>
            <li>Każde zapytanie jest audytowane. Limit: 10 zapytań / minutę.</li>
            <li>
              Zwrócony „dzień pobrania" (UTC) to dzień zserwowania znaku
              wodnego temu kontu — poszlaka wskazująca prawdopodobny moment
              pobrania, nie bezpośredni dowód skopiowania treści.
            </li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="suspectText"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Podejrzany tekst
            </label>
            <textarea
              id="suspectText"
              value={suspectText}
              onChange={(e) => setSuspectText(e.target.value)}
              disabled={mutation.isPending}
              rows={6}
              maxLength={MAX_SUSPECT_TEXT_LENGTH}
              spellCheck={false}
              autoComplete="off"
              placeholder="Wklej tutaj tekst skopiowany z serwisu konkurencji…"
              className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100"
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span
                className={
                  length === 0
                    ? "text-neutral-400"
                    : hasHidden
                    ? "text-green-600"
                    : "text-amber-600"
                }
              >
                {length === 0
                  ? "Wklej tekst, aby rozpocząć."
                  : hasHidden
                  ? "✓ Wykryto ukryte znaki w tekście."
                  : "⚠ Brak ukrytych znaków — kopia mogła zostać przepisana ręcznie."}
              </span>
              <span className="text-neutral-500">
                {length}/{MAX_SUSPECT_TEXT_LENGTH}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="since"
                className="block text-sm font-medium text-neutral-700 mb-1"
              >
                Od (opcjonalnie)
              </label>
              <input
                id="since"
                type="date"
                value={since}
                onChange={(e) => setSince(e.target.value)}
                disabled={mutation.isPending}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-100"
              />
            </div>
            <div>
              <label
                htmlFor="until"
                className="block text-sm font-medium text-neutral-700 mb-1"
              >
                Do (opcjonalnie)
              </label>
              <input
                id="until"
                type="date"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
                disabled={mutation.isPending}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-100"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={!canSubmit}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium ${
                canSubmit
                  ? "bg-primary-600 text-white hover:bg-primary-700"
                  : "bg-neutral-100 text-neutral-400 cursor-not-allowed"
              }`}
            >
              {mutation.isPending ? "Odzyskiwanie…" : "Odzyskaj użytkownika"}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={mutation.isPending}
              className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              Wyczyść
            </button>
          </div>
        </form>

        {result && (
          <div aria-live="polite">
            {result.resolved ? (
              <div className="bg-green-50 border border-green-200 rounded-md p-4 space-y-3">
                <h3 className="text-green-800 font-medium">
                  Odnaleziono użytkownika
                </h3>
                <ResultRow
                  label="User ID"
                  value={result.userId ?? "—"}
                  onCopy={() =>
                    result.userId && copy(result.userId, "User ID")
                  }
                />
                <ResultRow
                  label="Email"
                  value={result.email ?? "—"}
                  onCopy={() => result.email && copy(result.email, "email")}
                />
                {result.copyDate && (
                  <ResultRow
                    label="Dzień pobrania (UTC)"
                    value={
                      result.copyNonce !== undefined
                        ? `${result.copyDate} (nonce ${result.copyNonce})`
                        : result.copyDate
                    }
                    hint={COPY_DAY_HINT}
                    onCopy={() => {
                      if (!result.copyDate) return;
                      const displayValue =
                        result.copyNonce !== undefined
                          ? `${result.copyDate} (nonce ${result.copyNonce})`
                          : result.copyDate;
                      copy(displayValue, "dzień pobrania");
                    }}
                  />
                )}
                {result.accessTimestamps &&
                  result.accessTimestamps.length > 0 && (
                    <div className="text-sm text-green-800">
                      <p className="font-medium">Znaczniki dostępu</p>
                      <ul className="list-disc list-inside mt-1 font-mono text-xs text-green-700">
                        {result.accessTimestamps.map((ts) => (
                          <li key={ts}>{ts}</li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            ) : (
              <UnresolvedPanel result={result} onCopy={copy} />
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

const ResultRow: React.FC<{
  label: string;
  value: string;
  onCopy: () => void;
  hint?: string;
}> = ({ label, value, onCopy, hint }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wide text-neutral-500">{label}</p>
      <p className="text-sm font-mono text-neutral-900 truncate">{value}</p>
      {hint && <p className="text-xs text-neutral-500 mt-0.5">{hint}</p>}
    </div>
    <button
      type="button"
      onClick={onCopy}
      className="shrink-0 rounded-md border border-neutral-300 px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
    >
      Kopiuj
    </button>
  </div>
);

const UnresolvedPanel: React.FC<{
  result: RecoverWatermarkResponse;
  onCopy: (value: string, label: string) => void;
}> = ({ result, onCopy }) => {
  const reason = result.reason ?? "NO_MATCH";
  const info = REASON_MESSAGES[reason] ?? REASON_MESSAGES.NO_MATCH;
  const isAmbiguous = reason === "AMBIGUOUS";
  const palette =
    isAmbiguous || reason === "NORMALIZED_BEYOND_RECOVERY"
      ? "bg-amber-50 border-amber-200 text-amber-800"
      : "bg-neutral-50 border-neutral-200 text-neutral-800";

  return (
    <div className={`border rounded-md p-4 space-y-3 ${palette}`}>
      <h3 className="font-medium">{info.title}</h3>
      <p className="text-sm">{info.detail}</p>
      {isAmbiguous && result.candidates && result.candidates.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            Kandydaci ({result.candidates.length}):
          </p>
          <p className="text-xs text-amber-700">{COPY_DAY_HINT}</p>
          {result.candidates.map((candidate) => (
            <div
              key={candidate.userId}
              className="flex items-center justify-between gap-3 rounded-md bg-white/60 border border-amber-200 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-mono text-neutral-900 truncate">
                  {candidate.userId}
                </p>
                <p className="text-xs text-neutral-600 truncate">
                  {candidate.email}
                </p>
                <p className="text-xs text-neutral-600 truncate">
                  Dzień: {candidate.copyDate} (nonce {candidate.copyNonce})
                </p>
              </div>
              <div className="shrink-0 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    onCopy(
                      `${candidate.copyDate} (nonce ${candidate.copyNonce})`,
                      "dzień pobrania"
                    )
                  }
                  className="rounded-md border border-neutral-300 px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
                >
                  Kopiuj dzień
                </button>
                <button
                  type="button"
                  onClick={() => onCopy(candidate.userId, "User ID")}
                  className="rounded-md border border-neutral-300 px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
                >
                  Kopiuj ID
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WatermarkRecovery;
